import { Component, OnInit, OnDestroy, TransferState, PLATFORM_ID, Inject, makeStateKey, Renderer2 } from '@angular/core';
import { DOCUMENT, isPlatformServer } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { AppState } from '@shared/redux/config-store-state.model';
import { emitAlert } from '@shared/redux/actions/actions-store-state.model';
import { AuthFirebaseService } from '@shared/services/auth-firebase.service';
import { BucketS3Service } from '@shared/services/bucket-s3.service';
import { FunctionsService } from '@shared/services/functions.service';
import { PricingService } from '@shared/services/pricing.service';
import { BookApiService } from '@modules/book/book-api.service';
import { SeoMetadataService } from '@shared/services/seo-metadata.service';
import { AuthorRoleService } from '@shared/services/author-role.service';
import { CartStorageService } from '@shared/services/cart-storage.service';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';

const BOOK_KEY = makeStateKey<any>('BOOK');

@Component({
  selector: 'astrea-book',
  templateUrl: './book.component.html',
  styleUrls: ['./book.component.scss']
})
export class BookComponent implements OnInit, OnDestroy {

  public bookcode!: string | null;
  public book!: any;
  public isPublicDoctrine = false;
  public isMagazine = false;

  public authors!: any;

  public loading = true;
  public loadingFile = {
    loading: false,
    type: ''
  };

  public wish = false;
  public loadingWish = false;
  private wishlistBook$: Subject<boolean> = new Subject();

  public isLoggedIn: boolean = false;
  private auth$!: Subscription;

  constructor(
    public S3Service: BucketS3Service,
    public fnService: FunctionsService,
    public pricingService: PricingService,
    private api: BookApiService,
    private router: Router,
    private route: ActivatedRoute,
    private store: Store<AppState>,
    private seo: SeoMetadataService,
    private authorRoleService: AuthorRoleService,
    private cartStorageService: CartStorageService,
    private authFirebaseService: AuthFirebaseService,
    private renderer: Renderer2,
    private _t: TranslateService,
    private sanitizer: DomSanitizer,
    private transferState: TransferState,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) { }

  ngOnInit(): void {
    this.bookcode = this.route.snapshot.paramMap.get('bookcode');
    if (!this.bookcode) {
      this.router.navigate(['/home']);
      return;
    }

    this.isPublicDoctrine = this.bookcode ? this.bookcode.startsWith('9900') : false;
    this.loadBook();
    this.userIsLoggedIn();
    this.listenWishlistBook();
  }

  ngOnDestroy(): void {
    if (this.auth$) this.auth$.unsubscribe();
    if (this.wishlistBook$) this.wishlistBook$.complete();
  }

  userIsLoggedIn(): void {
    this.auth$ = this.authFirebaseService.isLoggedIn()
      .subscribe({
        next: user => {
          this.isLoggedIn = user === null ? false : true;
        }
      });
  }

  setMetadata(): void {
    if (this.book) {
      const rawDescription = this.book?.subtitle || this.book?.description || this.book?.title || '';
      const trimmedDescription = this.fnService.truncateText(rawDescription, 150);
      this.seo.setMetadata({
        title: `${this.book.title}`,
        description: trimmedDescription,
        relativeUrl: `/book/${this.bookcode}`
      });
      this.setSchemas();
    }
  }

  setSchemas(): void {
    try {
      const physicalBookSchema = this.seo.createPhysicalBookSchema(
        this.book,
        this.authors,
        this.getBookType(this.book.bookType),
        this.getBookBinding(this.book.bookBinding) || 'Tapa dura',
        `${this.book.edition}${this.fnService.getOrdinalNumber(this.book.edition)}`
      );
      const script = this.renderer.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(physicalBookSchema);
      this.renderer.appendChild(this.document.head, script);

      if (this.book.useAV && !this.book.private) {
        const digitalBookSchema = this.seo.createDigitalBookSchema(
          this.book,
          this.authors,
          this.pricingService.getAVPricing(this.book),
          this.getBookType(this.book.bookType),
          `${this.book.edition}${this.fnService.getOrdinalNumber(this.book.edition)}`
        );
        const script2 = this.renderer.createElement('script');
        script2.type = 'application/ld+json';
        script2.text = JSON.stringify(digitalBookSchema);
        this.renderer.appendChild(this.document.head, script2);
      }
    } catch (err) {
      console.log(err)
    }
  }

  loadBook(): void {
    const cachedData = this.transferState.get(BOOK_KEY, null);
    if (cachedData) {
      this.book = cachedData.result.data;
      this.isMagazine = this.book.bookType == 'M';
      this.buildAuthorsData();
      this.setMetadata();
      this.loading = false;

      setTimeout(() => {
        if (this.isLoggedIn)
          this.getWishlistVerify();
      }, 1000);
      this.transferState.remove(BOOK_KEY);
    } else {
      this.getBookDetails();
    }
  }

  getBookDetails(): void {
    this.loading = true;
    this.api.getBookDetails(this.bookcode)
      .pipe(
        tap(data => {
          if (isPlatformServer(this.platformId)) {
            this.transferState.set(BOOK_KEY, data);
          }
        })
      )
      .subscribe({
        next: res => {
          this.book = res.result.data;
          this.isMagazine = this.book.bookType == 'M';
          this.buildAuthorsData();
          this.setMetadata();
          this.loading = false;

          setTimeout(() => {
            // ASYNC: puede malir sal
            if (this.isLoggedIn)
              this.getWishlistVerify();
          }, 1000);
        },
        error: () => {
          this.router.navigate(['/home']);
        }
      });
  }

  getWishlistVerify(): void {
    this.api.getWishlistVerify(this.book.bookcode)
      .subscribe({
        next: res => {
          this.wish = res.result.data ? true : false;
        }
      });
  }

  getFile(bucket: string, bookcode: string): void {
    this.loadingFile.loading = true;
    this.loadingFile.type = bucket;
    this.api.getFile({ bucket, bookcode })
      .then(pdf => {
        if (pdf.type.includes('application/json')) {
          this.alertFileError();
        } else if (pdf.type.includes('application/pdf')) {
          window.open(URL.createObjectURL(pdf), '_blank');
        }
        this.loadingFile.loading = false;
        this.loadingFile.type = '';
      })
      .catch(err => {
        this.alertFileError();
      });
  }

  /* ********** CART ********** */

  handlePhysicalBook(): void {
    this.cartStorageService.addCartStorage({
      bookcode: this.book.bookcode,
      typeStorage: 'physical_book',
      count: 1
    });
    if (this.isLoggedIn)
      this.alertStorage();
    else
      this.alertLoginStorage();
  }

  handleDigitalBook(): void {
    this.cartStorageService.addCartStorage({
      bookcode: this.book.bookcode,
      typeStorage: 'digital_book',
      count: 3
    });
    if (this.isLoggedIn)
      this.alertStorage();
    else
      this.alertLoginStorage();
  }

  alertStorage(): void {
    this.store.dispatch(emitAlert({
      message: this._t.instant('alert.cart.add'),
      alertType: 'message'
    }));
  }

  alertLoginStorage(): void {
    this.store.dispatch(emitAlert({
      message: this._t.instant('alert.cart.add_login'),
      alertType: 'message'
    }));
  }


  /* ********** WISHLIST ********** */

  handleWishlist(): void {
    if (this.isLoggedIn)
      this.wishlistBook$.next(true);
    else
      this.alertLoginWishlist();
  }

  listenWishlistBook(): void {
    this.wishlistBook$
      .pipe(
        debounceTime(350)
      )
      .subscribe({
        next: () => {
          this.loadingWish = true;
          this.api.addWishlist({ bookcode: this.book.bookcode })
            .subscribe({
              next: () => {
                this.wish = true;
                this.loadingWish = false;
                this.alertAddWishlist();
              }
            });
        }
      });
  }

  alertAddWishlist(): void {
    this.store.dispatch(emitAlert({
      message: this._t.instant('alert.wishlist.add'),
      alertType: 'success'
    }));
  }

  alertLoginWishlist(): void {
    this.store.dispatch(emitAlert({
      message: this._t.instant('alert.wishlist.add_login'),
      alertType: 'message'
    }));
  }


  /* ********** FUNCTIONS ********** */

  buildAuthorsData(): void {
    this.authors = this.authorRoleService.buildAuthorsAndRoles(this.book.authors, this.book.vAuthor);
  }

  getAuthorsRelatedBook(related: any): any[] {
    if (related.authors && related.vAuthor)
      return this.authorRoleService.buildAuthorsAndRoles(related.authors, related.vAuthor).slice(0, 2);  // Mostramos los dos autores más importantes
    else
      return [];
  }

  buildLMData(): string {
    return this.book.vLegalMatter.map((a: any) => { return `${a.legalMatter}` }).join(' - ');
  }

  getAstreaVirtualURL() {
    return this.sanitizer.bypassSecurityTrustUrl(`https://astreavirtual.com/reader/${this.bookcode}`);
  }

  getBookType(booktype: string): string {
    switch (booktype) {
      case 'A' : case 'D' : { return this._t.instant('books.type.doctrine'); }
      case 'B' : { return this._t.instant('books.type.book'); }
      case 'M' : { return this._t.instant('books.type.magazine'); }
      case 'J' : { return this._t.instant('books.type.magazine'); }
      default : { return this._t.instant('books.type.book') }
    }
  }

  getBookBinding(booktype: string): string | null {
    switch (booktype) {
      case 'E' : { return this._t.instant('books.binding.bound'); }
      case 'D' : { return this._t.instant('books.binding.doctrine'); }
      case 'R' : { return this._t.instant('books.binding.rustic_cover'); }
      default : { return null }
    }
  }

  alertFileError(): void {
    this.store.dispatch(emitAlert({
      message: this._t.instant('alert.file_err'),
      alertType: 'danger'
    }));
  }

}
